<template>
  <div class="apply-page">
    <!-- Admin Authentication Modal -->
    <div v-if="showAdminAuth" class="auth-modal">
      <AdminAuth @authenticated="handleAdminAuth" />
    </div>

    <!-- Main Content: nav is a sibling of the padded column so the bar is full-bleed -->
    <template v-else>
      <PublicSiteNav />
      <div class="landing-container">
      <p v-if="inboxAccessDenied" class="inbox-access-denied-banner" role="status">
        The booking inbox admin page is restricted. Other admin areas are unchanged.
      </p>
      <p v-if="clubsAccessDenied" class="inbox-access-denied-banner" role="status">
        The clubs / venues admin page is restricted to the primary operator account.
      </p>
      <div class="landing-header">
        <h1>🎤 Hilarity Unlimited Comedian Sign Up</h1>
        <p class="subtitle">Choose which comedy night you'd like to apply for</p>
      </div>

      <div v-if="isLoading" class="loading">Loading venues...</div>

      <div v-else class="venues-grid">
      <div
        v-for="venue in venues"
        :key="venue.id"
        class="venue-card"
        @click="goToVenue(venue.venue_slug)"
      >
        <div class="venue-logo">
          <img :src="logoFor(venue)" :alt="venue.venue_name + ' logo'" />
        </div>
        <h2>{{ venue.venue_name }}</h2>
        <div class="venue-details">
          <p class="venue-day">{{ getDayLabel(venue) }}</p>
          <p class="venue-location">📍 {{ venue.venue_location }}</p>
          <p class="venue-description">{{ venue.description }}</p>
        </div>
        <button class="apply-button">
          {{ getApplyLabel(venue) }} →
        </button>
      </div>
    </div>

      <div v-if="!isLoading && venues.length === 0" class="no-venues">
        <p>No venues available at the moment.</p>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
/** @see docs/APP_UI_SPEC.md — F1 (apply hub omits archived venues; same for logged-in admins), G1 (`/apply`) */
import { supabase } from '../supabaseClient';
import AdminAuth from './AdminAuth.vue';
import PublicSiteNav from './PublicSiteNav.vue';
import { venueDayLabel, venueApplyLabel } from '../utils/venueDisplay';
import { venuesVisibleOnPublicHome } from '../utils/venuePublicList';
import { venueLogoSrc } from '../utils/venueLogo.js';

export default {
  name: 'LandingPage',
  components: {
    AdminAuth,
    PublicSiteNav,
  },
  data() {
    return {
      venues: [],
      isLoading: true,
      showAdminAuth: false,
      pendingRedirect: null,
      inboxAccessDenied: false,
      clubsAccessDenied: false,
    };
  },
  async created() {
    if (this.$route.query.auth === 'required') {
      this.showAdminAuth = true;
      this.pendingRedirect = this.$route.query.redirect;
    } else {
      if (this.$route.query.auth === 'inbox_denied') {
        this.inboxAccessDenied = true;
        this.$router.replace({ query: {} });
      }
      if (this.$route.query.auth === 'clubs_denied') {
        this.clubsAccessDenied = true;
        this.$router.replace({ query: {} });
      }
      await this.fetchVenues();
    }
  },
  methods: {
    async fetchVenues() {
      try {
        const { data, error } = await supabase
          .from('venues')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;

        // Admins use an authenticated Supabase client: RLS returns all venues.
        // Public home must still hide archived rows (same as anon).
        this.venues = venuesVisibleOnPublicHome(data);
      } catch (err) {
        console.error('Error fetching venues:', err);
      } finally {
        this.isLoading = false;
      }
    },

    goToVenue(slug) {
      this.$router.push(`/${slug}`);
    },

    logoFor: venueLogoSrc,

    getDayLabel(venue) {
      return venueDayLabel(venue);
    },

    getApplyLabel(venue) {
      return venueApplyLabel(venue);
    },

    handleAdminAuth() {
      this.showAdminAuth = false;
      if (this.pendingRedirect) {
        this.$router.push(this.pendingRedirect);
      }
    }
  }
};
</script>

<style scoped>
.apply-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.landing-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
}

.inbox-access-denied-banner {
  margin: 0 0 1rem;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.95);
  color: #495057;
  font-size: 0.95rem;
  text-align: center;
}

.landing-header {
  text-align: center;
  margin-bottom: 50px;
  color: white;
}

.landing-header h1 {
  font-size: 48px;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.subtitle {
  font-size: 20px;
  opacity: 0.95;
}

.loading {
  text-align: center;
  padding: 60px;
  color: white;
  font-size: 18px;
}

.venues-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

.venue-card {
  background: white;
  border-radius: 16px;
  padding: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.venue-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.venue-logo {
  width: 200px;
  height: 200px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.venue-logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.venue-card h2 {
  color: #333;
  font-size: 28px;
  margin-bottom: 20px;
}

.venue-details {
  margin-bottom: 30px;
}

.venue-day {
  color: #667eea;
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
}

.venue-location {
  color: #666;
  font-size: 16px;
  margin-bottom: 15px;
}

.venue-description {
  color: #888;
  font-size: 15px;
  line-height: 1.6;
  margin-top: 15px;
}

.apply-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
}

.apply-button:hover {
  transform: scale(1.05);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

.no-venues {
  text-align: center;
  padding: 60px;
  color: white;
  font-size: 18px;
}

@media (max-width: 768px) {
  .landing-header h1 {
    font-size: 36px;
  }

  .subtitle {
    font-size: 16px;
  }

  .venues-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .venue-card {
    padding: 30px 20px;
  }

  .venue-card h2 {
    font-size: 24px;
  }
}

.auth-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
</style>
